import React from 'react';
import {
  getCertificateTextFontSize,
  loadCertificateTemplate,
} from '../../admin-page/pages/facilitator/pages/hybrid/certificateTemplate';

const ParentCertificateTemplateUrl =
  '/resource-library/EzParent_Certificate_V3.svg';

const ParentCertificateDownloadLink = React.lazy(
  () => import('./ParentCertificateDownloadLink'),
);

const ParentCertificateTemplateOptions = {
  // V3 converts its sample date and parent name to outlined paths.
  excludedPathIndexes: [3, 228],
  textTargets: {
    name: {
      x: 396,
      y: 331,
      dominantBaseline: 'middle',
      textAnchor: 'middle',
      fontSize: 28,
      fill: '#000000',
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontWeight: 700,
      pdfFontFamily: 'Helvetica-Bold',
    },
    date: {
      x: 396,
      y: 431,
      dominantBaseline: 'middle',
      textAnchor: 'middle',
      fontSize: 16,
      fill: '#000000',
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontWeight: 700,
      pdfFontFamily: 'Helvetica-Bold',
    },
    facilitators: {
      x: 396,
      y: 458,
      dominantBaseline: 'middle',
      textAnchor: 'middle',
      fontSize: 11,
      fill: '#282E68',
      fontFamily: 'Helvetica, Arial, sans-serif',
      fontWeight: 600,
      pdfFontFamily: 'Helvetica-Bold',
    },
  },
};

const SpanishParentCertificateTemplateOptions = {
  ...ParentCertificateTemplateOptions,
  // The Spanish SVG outlines its sample name and date in different paths.
  excludedPathIndexes: [238, 239],
  textTargets: {
    ...ParentCertificateTemplateOptions.textTargets,
    name: {
      ...ParentCertificateTemplateOptions.textTargets.name,
      y: 324,
    },
    date: {
      ...ParentCertificateTemplateOptions.textTargets.date,
      y: 445,
    },
    facilitators: {
      ...ParentCertificateTemplateOptions.textTargets.facilitators,
      y: 471,
    },
  },
};

const formatCertificateDate = (completedAt) => {
  const dateParts = String(completedAt || '').match(
    /^(\d{4})-(\d{2})-(\d{2})/,
  );

  if (dateParts) {
    const [, year, month, day] = dateParts;
    return `${month}/${day}/${year}`;
  }

  const completionDate = completedAt ? new Date(completedAt) : null;

  if (!completionDate || Number.isNaN(completionDate.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US').format(completionDate);
};

const formatFacilitatorNames = (facilitatorNames) => {
  const names = Array.from(
    new Set(
      (Array.isArray(facilitatorNames) ? facilitatorNames : [])
        .map((facilitator) => {
          if (typeof facilitator === 'string') {
            return facilitator.trim();
          }

          return (
            facilitator?.name ||
            facilitator?.fullName ||
            [facilitator?.firstName, facilitator?.lastName]
              .filter(Boolean)
              .join(' ')
          ).trim();
        })
        .filter(Boolean),
    ),
  );

  if (names.length < 2) {
    return names[0] || '';
  }

  if (names.length === 2) {
    return names.join(' & ');
  }

  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
};

const getCertificateFileName = (name) => {
  const safeName = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return `${safeName || 'parent'}-ezparent-certificate.pdf`;
};

const CertificateArtwork = ({ template, parentName, textValues }) => (
  <svg
    viewBox={template.viewBox}
    width={template.width}
    height={template.height}
    role='img'
    aria-label={`ezParent certificate for ${parentName}`}
    className='h-auto w-full rounded border border-slate-300 bg-white'
  >
    {template.elements.map((element) =>
      element.type === 'image' ? (
        <image
          key={element.key}
          href={element.source}
          x={element.x}
          y={element.y}
          width={element.width}
          height={element.height}
          preserveAspectRatio={element.preserveAspectRatio}
        />
      ) : (
        <path
          key={element.key}
          d={element.d}
          fill={element.fill}
          stroke={element.stroke}
          strokeWidth={element.strokeWidth}
          strokeMiterlimit={element.strokeMiterlimit}
        />
      ),
    )}
    {Object.entries(textValues)
      .filter(([, textValue]) =>
        String(textValue.value || '').trim(),
      )
      .map(([id, textValue]) => {
        const target = template.textTargets[id];

        return (
          <text
            key={id}
            id={id}
            x={target.x}
            y={target.y}
            dominantBaseline={target.dominantBaseline}
            textAnchor={target.textAnchor}
            fontFamily={target.fontFamily}
            fontSize={textValue.fontSize || target.fontSize}
            fontWeight={target.fontWeight}
            fontStyle={target.fontStyle}
            fill={target.fill}
          >
            {textValue.value}
          </text>
        );
      })}
  </svg>
);

const ParentCertificate = ({
  parentName = localStorage.getItem('name')?.trim() || 'Parent',
  completedAt,
  facilitatorNames = [],
  compact = false,
  disabled = false,
  className = 'btn-1 download-cert',
  language = 'en',
}) => {
  const certificateLanguage = language === 'es' ? 'es' : 'en';
  const [loadedTemplate, setLoadedTemplate] = React.useState(null);
  const template =
    loadedTemplate?.language === certificateLanguage
      ? loadedTemplate.template
      : null;
  const [templateError, setTemplateError] = React.useState('');
  const facilitatorText = formatFacilitatorNames(facilitatorNames);
  const facilitatorCertificateText = facilitatorText
    ? `${certificateLanguage === 'es' ? 'Facilitador(es) del grupo' : 'Group Facilitator(s)'}: ${facilitatorText}`
    : '';
  const textValues = {
    name: {
      value: parentName,
      fontSize: getCertificateTextFontSize(parentName, {
        maximumFontSize: 28,
        minimumFontSize: 14,
        maxWidth: 440,
      }),
    },
    date: {
      value: formatCertificateDate(completedAt),
      fontSize: 16,
    },
    facilitators: {
      value: facilitatorCertificateText,
      fontSize: getCertificateTextFontSize(facilitatorCertificateText, {
        maximumFontSize: 11,
        minimumFontSize: 8,
        maxWidth: 620,
      }),
    },
  };

  React.useEffect(() => {
    let isCurrent = true;
    setTemplateError('');

    loadCertificateTemplate(
      certificateLanguage === 'es'
        ? '/resource-library/EzTraining_Certificate_es.svg'
        : ParentCertificateTemplateUrl,
      certificateLanguage === 'es'
        ? SpanishParentCertificateTemplateOptions
        : ParentCertificateTemplateOptions,
    )
      .then((loadedTemplate) => {
        if (isCurrent) {
          setLoadedTemplate({
            language: certificateLanguage,
            template: loadedTemplate,
          });
        }
      })
      .catch((error) => {
        if (isCurrent) {
          setTemplateError(error.message);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [certificateLanguage]);

  if (compact) {
    if (disabled || !template) {
      return (
        <button
          type='button'
          className={className}
          disabled
          title={templateError || undefined}
        >
          {templateError ? 'Certificate unavailable' : 'Download Certificate'}
        </button>
      );
    }

    return (
      <React.Suspense
        fallback={
          <button type='button' className={className} disabled>
            Preparing Certificate...
          </button>
        }
      >
        <ParentCertificateDownloadLink
          language={certificateLanguage}
          template={template}
          parentName={parentName}
          textValues={textValues}
          fileName={getCertificateFileName(parentName)}
          className={className}
        >
          {({ loading, error }) =>
            error
              ? 'Certificate unavailable'
              : loading
                ? 'Preparing Certificate...'
                : 'Download Certificate'
          }
        </ParentCertificateDownloadLink>
      </React.Suspense>
    );
  }

  const renderCertificate = (statusMessage) => (
    <>
      <div className='border-b border-slate-300 bg-white px-4 py-3 text-center text-lg text-slate-900'>
        {statusMessage}
      </div>
      <div className='pointer-events-none bg-white p-4'>
        {template ? (
          <CertificateArtwork
            template={template}
            parentName={parentName}
            textValues={textValues}
          />
        ) : (
          <div
            aria-hidden='true'
            className='w-full rounded border border-slate-200 bg-slate-50'
            style={{ aspectRatio: '792 / 612' }}
          />
        )}
      </div>
    </>
  );

  return (
    <section className='mx-auto mt-8 w-full max-w-2xl text-left'>
      <h3 className='mb-4 text-2xl font-semibold'>Certificate of Completion</h3>
      {template ? (
        <React.Suspense
          fallback={
            <div className='overflow-hidden rounded-lg border border-slate-300 bg-white shadow'>
              {renderCertificate('Preparing your printable certificate...')}
            </div>
          }
        >
          <ParentCertificateDownloadLink
            language={certificateLanguage}
            template={template}
            parentName={parentName}
            textValues={textValues}
            fileName={getCertificateFileName(parentName)}
            className='block overflow-hidden rounded-lg border border-slate-300 bg-white no-underline shadow transition hover:border-purple hover:shadow-md'
          >
            {({ loading, error }) =>
              renderCertificate(
                error
                  ? 'Unable to prepare the certificate. Please try again.'
                  : loading
                    ? 'Preparing your printable certificate...'
                    : 'Download your printable ezParent certificate',
              )
            }
          </ParentCertificateDownloadLink>
        </React.Suspense>
      ) : (
        <div className='overflow-hidden rounded-lg border border-slate-300 bg-white shadow'>
          {renderCertificate(
            templateError
              ? 'Unable to load the certificate. Please refresh and try again.'
              : 'Preparing your printable certificate...',
          )}
        </div>
      )}
      {templateError ? (
        <p role='alert' className='mt-3 text-sm text-red-700'>
          {templateError}
        </p>
      ) : null}
    </section>
  );
};

export default ParentCertificate;
